import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { gsap } from "gsap";

const MAX_ZOOM = 3;
const SWIPE_DISTANCE = 52;

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const getDistance = (first, second) => Math.hypot(second.x - first.x, second.y - first.y);

const getCenter = (first, second) => ({
  x: (first.x + second.x) / 2,
  y: (first.y + second.y) / 2,
});

export function ImageLightbox({ images, initialIndex = 0, title, onClose }) {
  const slides = useMemo(() => images.filter(Boolean), [images]);
  const normalizeIndex = useCallback(
    (index) => ((index % slides.length) + slides.length) % slides.length,
    [slides.length]
  );
  const [currentIndex, setCurrentIndex] = useState(() => normalizeIndex(initialIndex));
  const [direction, setDirection] = useState("next");
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const imageRef = useRef(null);
  const closeRef = useRef(null);
  const closingRef = useRef(false);
  const closeActionRef = useRef(null);
  const navigateActionRef = useRef(null);
  const transformRef = useRef({ scale: 1, x: 0, y: 0 });
  const gestureRef = useRef({
    pointers: new Map(),
    mode: null,
    startX: 0,
    startY: 0,
    startPanX: 0,
    startPanY: 0,
    startScale: 1,
    startDistance: 0,
    startCenter: { x: 0, y: 0 },
  });
  const currentImage = slides[currentIndex];
  const hasMultipleSlides = slides.length > 1;

  const applyTransform = useCallback((nextTransform, animate = false) => {
    const image = imageRef.current;
    const scale = clamp(nextTransform.scale, 1, MAX_ZOOM);
    const maxX = image ? (image.clientWidth * (scale - 1)) / 2 : 0;
    const maxY = image ? (image.clientHeight * (scale - 1)) / 2 : 0;
    const x = scale === 1 ? 0 : clamp(nextTransform.x, -maxX, maxX);
    const y = scale === 1 ? 0 : clamp(nextTransform.y, -maxY, maxY);

    transformRef.current = { scale, x, y };
    if (!image) return;
    image.style.transition = animate ? "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)" : "none";
    image.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  }, []);

  const resetTransform = useCallback((animate = true) => {
    applyTransform({ scale: 1, x: 0, y: 0 }, animate);
  }, [applyTransform]);

  const navigate = useCallback((step) => {
    if (!hasMultipleSlides) return;
    setDirection(step > 0 ? "next" : "previous");
    resetTransform(false);
    gestureRef.current.pointers.clear();
    gestureRef.current.mode = null;
    setCurrentIndex((index) => normalizeIndex(index + step));
  }, [hasMultipleSlides, normalizeIndex, resetTransform]);

  navigateActionRef.current = navigate;

  useLayoutEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const isTestEnvironment = navigator.userAgent.toLowerCase().includes("jsdom");
    if (reduceMotion || isTestEnvironment) return undefined;

    const context = gsap.context(() => {
      gsap.timeline()
        .fromTo(rootRef.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.34, ease: "power3.out" })
        .fromTo(
          stageRef.current,
          { clipPath: "inset(7% 7% 7% 7%)", scale: 0.94, y: 20 },
          { clipPath: "inset(0% 0% 0% 0%)", scale: 1, y: 0, duration: 0.62, ease: "expo.out" },
          0.04
        );
    }, rootRef);

    return () => context.revert();
  }, []);

  useLayoutEffect(() => {
    resetTransform(false);
  }, [currentIndex, resetTransform]);

  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const isTestEnvironment = navigator.userAgent.toLowerCase().includes("jsdom");
    if (reduceMotion || isTestEnvironment) {
      onClose();
      return;
    }

    gsap.timeline({ onComplete: onClose })
      .to(stageRef.current, { autoAlpha: 0, scale: 0.97, y: 12, duration: 0.3, ease: "power3.in" })
      .to(rootRef.current, { autoAlpha: 0, duration: 0.28, ease: "power2.inOut" }, 0);
  };

  closeActionRef.current = requestClose;

  useEffect(() => {
    const previousFocus = document.activeElement;
    const caseStudy = document.querySelector(".case-study");
    caseStudy?.setAttribute("aria-hidden", "true");
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeActionRef.current?.();
        return;
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        navigateActionRef.current?.(event.key === "ArrowRight" ? 1 : -1);
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = Array.from(rootRef.current.querySelectorAll("button:not([disabled])"));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      caseStudy?.removeAttribute("aria-hidden");
      previousFocus?.focus?.();
    };
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const preventNativeGesture = (event) => event.preventDefault();
    stage.addEventListener("gesturestart", preventNativeGesture, { passive: false });
    stage.addEventListener("gesturechange", preventNativeGesture, { passive: false });
    stage.addEventListener("gestureend", preventNativeGesture, { passive: false });
    return () => {
      stage.removeEventListener("gesturestart", preventNativeGesture);
      stage.removeEventListener("gesturechange", preventNativeGesture);
      stage.removeEventListener("gestureend", preventNativeGesture);
    };
  }, []);

  const beginSingleGesture = (pointer) => {
    const gesture = gestureRef.current;
    const transform = transformRef.current;
    gesture.mode = transform.scale > 1 ? "pan" : "swipe";
    gesture.startX = pointer.x;
    gesture.startY = pointer.y;
    gesture.startPanX = transform.x;
    gesture.startPanY = transform.y;
  };

  const beginPinchGesture = () => {
    const gesture = gestureRef.current;
    const [first, second] = Array.from(gesture.pointers.values());
    gesture.mode = "pinch";
    gesture.startDistance = Math.max(getDistance(first, second), 1);
    gesture.startScale = transformRef.current.scale;
    gesture.startCenter = getCenter(first, second);
    gesture.startPanX = transformRef.current.x;
    gesture.startPanY = transformRef.current.y;
  };

  const handlePointerDown = (event) => {
    if (event.pointerType === "mouse") return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const gesture = gestureRef.current;
    gesture.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (gesture.pointers.size === 1) beginSingleGesture({ x: event.clientX, y: event.clientY });
    if (gesture.pointers.size === 2) beginPinchGesture();
  };

  const handlePointerMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture.pointers.has(event.pointerId)) return;
    event.preventDefault();
    gesture.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (gesture.pointers.size >= 2) {
      if (gesture.mode !== "pinch") beginPinchGesture();
      const [first, second] = Array.from(gesture.pointers.values());
      const center = getCenter(first, second);
      const scale = gesture.startScale * (getDistance(first, second) / gesture.startDistance);
      applyTransform({
        scale,
        x: gesture.startPanX + center.x - gesture.startCenter.x,
        y: gesture.startPanY + center.y - gesture.startCenter.y,
      });
      return;
    }

    if (gesture.mode === "pan") {
      applyTransform({
        scale: transformRef.current.scale,
        x: gesture.startPanX + event.clientX - gesture.startX,
        y: gesture.startPanY + event.clientY - gesture.startY,
      });
    }
  };

  const finishPointer = (event, cancelled = false) => {
    const gesture = gestureRef.current;
    const pointer = gesture.pointers.get(event.pointerId);
    if (!pointer) return;
    const wasPinching = gesture.mode === "pinch";
    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    gesture.pointers.delete(event.pointerId);

    if (gesture.pointers.size === 1) {
      beginSingleGesture(Array.from(gesture.pointers.values())[0]);
      return;
    }

    if (gesture.pointers.size === 0) {
      if (
        !cancelled &&
        !wasPinching &&
        gesture.mode === "swipe" &&
        Math.abs(deltaX) >= SWIPE_DISTANCE &&
        Math.abs(deltaX) > Math.abs(deltaY) * 1.15
      ) {
        navigate(deltaX < 0 ? 1 : -1);
      } else {
        applyTransform(transformRef.current, true);
      }
      gesture.mode = null;
    }
  };

  if (!currentImage) return null;

  const handleBackdrop = (event) => {
    if (
      event.target === rootRef.current ||
      event.target === stageRef.current ||
      event.target.classList?.contains("image-lightbox__media")
    ) {
      requestClose();
    }
  };

  return createPortal(
    <div
      className="image-lightbox"
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} screenshot ${currentIndex + 1} of ${slides.length}`}
      onMouseDown={handleBackdrop}
      onClick={handleBackdrop}
    >
      <div
        className="image-lightbox__stage"
        ref={stageRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={(event) => finishPointer(event, true)}
        onDoubleClick={() => resetTransform()}
      >
        <div className={`image-lightbox__media is-${direction}`} key={`${currentIndex}-${currentImage.src}`}>
          <img
            ref={imageRef}
            src={currentImage.src}
            alt={currentImage.alt}
            width={currentImage.width}
            height={currentImage.height}
            draggable="false"
            data-lightbox-image
          />
        </div>
      </div>

      {hasMultipleSlides && (
        <>
          <button
            className="image-lightbox__nav image-lightbox__nav--previous"
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Previous enlarged screenshot"
          >
            <span aria-hidden="true" />
          </button>
          <button
            className="image-lightbox__nav image-lightbox__nav--next"
            type="button"
            onClick={() => navigate(1)}
            aria-label="Next enlarged screenshot"
          >
            <span aria-hidden="true" />
          </button>
          <div className="image-lightbox__status" aria-live="polite">
            {String(currentIndex + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </div>
        </>
      )}

      <button
        className="image-lightbox__close"
        type="button"
        ref={closeRef}
        onClick={requestClose}
        aria-label="Close enlarged screenshot"
      >
        <span />
        <span />
      </button>
    </div>,
    document.body
  );
}
